import { fullStoryRecordingEnabled } from './fullstory-recording-flag';
import { isPublicRoute } from './public-routes';
import { resolveMount } from './resolve-mount';

/**
 * FullStory session recording, through Wix's shared `wix-recorder` loader (org 11CC00) — the same script
 * Wixel's web app loads. Never touch `window.FS` directly: wix-recorder owns the SDK, the identify (the Wix
 * user id) and the org, and its README forbids it.
 *
 * **Gate.** The Conductor feature toggle `symphonyFullStoryRecording` (the same id the phones conduct) decides
 * who is recorded, so `__forceRecording()` replaces wix-recorder's own Petri sampling. With the toggle off the
 * script is never requested. Production builds only, Symphony mount only, and never on a public route (`/s/`,
 * `/pack-open`, `/me/`: their readers are not our users) or on `/pack-accept`, whose URL carries the invite
 * token. Both are decided by the route the tab LANDED on, which is where a token-bearing URL arrives.
 *
 * **Masking: credentials only** — passwords and one-time codes. FullStory masks `type="password"` on its
 * own; a field that can be REVEALED (the DS `Input` on `kind="secure"`) and the OTP boxes also carry
 * `RECORDER_MASK_CLASS` (`lib/recorder-mask.ts`), so neither value reaches the replay.
 */
const WIX_RECORDER_SRC = 'https://static.parastorage.com/unpkg-semver/wix-recorder@^1/app.bundle.min.js';

type WixRecorder = {
  addLabel: (label: string) => void;
  addCustomAttribute: (key: string, value: string) => void;
  __forceRecording: () => void;
};

declare global {
  interface Window {
    wixRecorder?: WixRecorder;
  }
}

let started = false;
/** Attributes set before the script loaded, replayed in its `onload`. */
const pending = new Map<string, string>();

export function initFullStory(pathname: string): void {
  if (started) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (process.env.NODE_ENV !== 'production') return;
  if (resolveMount(window.location.pathname) === '/orion-web') return;
  if (isPublicRoute(pathname) || pathname.startsWith('/pack-accept')) return;
  started = true;

  void fullStoryRecordingEnabled().then((record) => {
    if (!record) {
      // Asked again on the next page load; one failed read must not disable recording for the tab's life.
      started = false;
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.dataset.smallApp = 'true';
    script.src = WIX_RECORDER_SRC;
    script.onload = () => {
      const recorder = window.wixRecorder;
      if (!recorder) return;
      recorder.addLabel('symphony-web');
      recorder.__forceRecording();
      // Tells Symphony from Wixel and the rest of Wix in the shared 11CC00 org — one filter, the same value
      // both phones set on their identify.
      recorder.addCustomAttribute('product', 'symphony');
      pending.forEach((value, key) => recorder.addCustomAttribute(key, value));
      pending.clear();
      // Not every recorded route reads `/api/me` itself (`/welcome`, `/profile-thread`), so ask here: it is what
      // names the recording with `symphonyUserId`. Imported lazily because `lib/me` imports this module.
      void import('./me').then(({ ensureMe }) => ensureMe());
    };
    document.head.appendChild(script);
  });
}

/** Tag the recording with something the Wix user id alone does not say. Safe before the script loads. */
export function setFullStoryAttribute(key: string, value: string | undefined): void {
  if (!value) return;
  const recorder = typeof window !== 'undefined' ? window.wixRecorder : undefined;
  if (recorder) recorder.addCustomAttribute(key, value);
  else pending.set(key, value);
}
